import type { Invite } from '@/api/types'

/// Staff invitations — what the panel shows and sends.
/// SPEC: specs/roles.md §6 «Invitations».

/// Accepted invitations stay on the staff page this long — enough to see
/// «Олена прийняла», short enough not to become a second staff list (the
/// person is in «Співробітники» now). Revoked ones are not shown at all.
const ACCEPTED_SHOWN_MS = 3 * 24 * 60 * 60 * 1000

/// What the invite panel opens with — empty, or what the add panel already
/// knew when it found no account and offered an invitation instead.
export type InviteDraft = Readonly<{
  email: string
  role: string | null
  scope: readonly string[]
  displayName: string
}>

export const EMPTY_INVITE: InviteDraft = { email: '', role: null, scope: [], displayName: '' }

export type InviteGroups = Readonly<{
  pending: readonly Invite[]
  expired: readonly Invite[]
  accepted: readonly Invite[]
}>

export const groupInvites = (invites: readonly Invite[], now: Date = new Date()): InviteGroups => ({
  pending: invites.filter((invite) => invite.status === 'pending'),
  expired: invites.filter((invite) => invite.status === 'expired'),
  accepted: invites.filter(
    (invite) =>
      invite.status === 'accepted' &&
      invite.acceptedAt !== null &&
      now.getTime() - Date.parse(invite.acceptedAt) < ACCEPTED_SHOWN_MS,
  ),
})

export const hasAnyInvite = (groups: InviteGroups): boolean =>
  groups.pending.length + groups.expired.length + groups.accepted.length > 0

/// An invitation token, as the server issues them (43 base64url characters),
/// or null. Sign-in takes `?invite=<token>` to come back to the invitation —
/// a token and not a path, so it can never be turned into a redirect to
/// somewhere else (`?next=//evil.example`).
export const inviteToken = (value: unknown): string | null =>
  typeof value === 'string' && /^[A-Za-z0-9_-]{43}$/.test(value) ? value : null

/// `/invite/<token>` → the token, for the session-ended redirect.
export const inviteTokenInPath = (path: string): string | null => {
  const match = /^\/invite\/([^/?#]+)\/?$/.exec(path)
  return match ? inviteToken(match[1]) : null
}

/// A nickname for an account made on the invitation page. services/auth
/// requires one (3–20 of a–z, 0–9, «.», «_», alphanumeric at both ends),
/// staff never see it in the panel, and the app lets them change it — so it
/// is made from the address plus digits rather than asked for.
export const nicknameFrom = (email: string, digits: string): string => {
  const local = email.split('@')[0] ?? ''
  const base = local.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12)
  return `${base.length >= 3 ? base : 'staff'}${digits.replace(/\D/g, '').slice(0, 6)}`
}

export const randomDigits = (n: number): string =>
  Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join('')
